import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";

type OverdueSample = {
  order_code: string;
  customer: string | null;
  cod: number;
  received_date: string | null;
  delivered_date: string | null;
  days: number | null;
};

type MismatchSample = {
  order_code: string;
  customer: string | null;
  cod: number;
  received_date: string | null;
  vtp_status: number | null;
  bk_cod: number;
};

type NoSettlementSample = {
  order_code: string;
  customer: string | null;
  cod: number;
  received_date: string | null;
};

type Stats = {
  today: string;
  month_start: string;
  revenue: {
    today: number;
    today_orders: number;
    month: number;
    month_orders: number;
    year: number;
    manual_year: number;
    manual_month: number;
    manual_by_channel: { channel: string; amount: number }[];
    carrier_today: number;
    carrier_today_orders: number;
    carrier_month: number;
    carrier_month_orders: number;
    carrier_year: number;
    carrier_year_orders: number;
    carrier_by_carrier: { carrier: string; orders: number; amount: number }[];
    revenue_by_carrier: {
      carrier: string;
      orders: number;
      total: number;
      month: number;
      year: number;
    }[];
    today_total: number;
    year_total: number;
    month_total: number;
    accurate_year: number;
    accurate_month: number;
    accurate_today: number;
    delivered_today: number;
    delivered_today_orders: number;
    delivered_month: number;
    delivered_month_orders: number;
    delivered_year: number;
    delivered_year_orders: number;
    vtp_delivered_today: number;
    vtp_delivered_today_orders: number;
    vtp_delivered_month: number;
    vtp_delivered_month_orders: number;
    vtp_delivered_year: number;
    vtp_delivered_year_orders: number;
  };
  latest_order_at: string | null;
  alerts: {
    cod_mismatch_count: number;
    cod_mismatch_loss: number;
    no_settlement_count: number;
    no_settlement_pending_cod: number;
    overdue_count: number;
    overdue_pending_cod: number;
    overdue_samples: OverdueSample[];
    cod_mismatch_samples: MismatchSample[];
    no_settlement_samples: NoSettlementSample[];
  };
  counts: {
    shops: number;
    industries: number;
    suppliers: number;
    products: number;
    invoices_this_month: number;
    invoices_total_this_month: number;
    settlements_this_month: number;
    locks_this_month: number;
  };
  recent_locks: {
    log_date: string;
    total_orders: number;
    total_cod_pos: number;
    total_settlement_received: number;
  }[];
  revenue_by_day: {
    date: string;
    orders: number;
    revenue: number;
  }[];
};

const fmtVND = (v: number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 0 }) + "đ";

const fmtJustDate = (s: string | null) =>
  s ? new Date(s + (s.length === 10 ? "T00:00:00" : "")).toLocaleDateString("vi-VN") : "—";

export function Dashboard() {
  const q = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => (await api.get<Stats>("/dashboard/stats")).data,
    // refetchInterval inherits from QueryClient default (15s)
  });
  const lastUpdate = q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null;

  if (q.isLoading) return <div className="text-gray-500">Đang tải...</div>;
  if (q.error || !q.data)
    return (
      <div className="text-red-600">
        {(q.error as any)?.response?.data?.detail ?? "Lỗi tải dữ liệu"}
      </div>
    );

  const s = q.data;
  const today = new Date(s.today + "T00:00:00").toLocaleDateString("vi-VN");
  const monthLabel = (() => {
    const d = new Date(s.month_start + "T00:00:00");
    return `Tháng ${d.getMonth() + 1}/${d.getFullYear()}`;
  })();

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Tổng quan</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Hôm nay {today} • {monthLabel}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <span
              className="text-xs text-slate-500"
              title="Tự refetch mỗi 15s"
            >
              Cập nhật {lastUpdate.toLocaleTimeString("vi-VN")}
            </span>
          )}
          <button
            onClick={() => q.refetch()}
            disabled={q.isFetching}
            className="btn-secondary"
          >
            {q.isFetching ? "↻ Đang tải..." : "↻ Refresh"}
          </button>
        </div>
      </div>

      {/* 3 chỉ số doanh thu: POScake (accrual) | VTP (đối chiếu portal) | ĐỐI SOÁT (cash) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="bg-green-50 border-2 border-green-400 rounded p-4">
          <div className="text-sm text-green-800 font-semibold">
            📦 ĐÃ GIAO (theo POScake)
          </div>
          <div className="text-[11px] text-gray-600 mb-2">
            Ngày shipper báo giao xong qua POScake — dùng cho Sổ S2a thuế
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <div className="text-gray-500">Hôm nay</div>
              <div className="font-mono text-base font-semibold text-green-900">
                {fmtVND(s.revenue.delivered_today)}
              </div>
              <div className="text-[10px] text-gray-500">
                {s.revenue.delivered_today_orders} đơn
              </div>
            </div>
            <div>
              <div className="text-gray-500">{monthLabel}</div>
              <div className="font-mono text-base font-semibold text-green-900">
                {fmtVND(s.revenue.delivered_month)}
              </div>
              <div className="text-[10px] text-gray-500">
                {s.revenue.delivered_month_orders} đơn
              </div>
            </div>
            <div>
              <div className="text-gray-500">Năm 2026</div>
              <div className="font-mono text-base font-semibold text-green-900">
                {fmtVND(s.revenue.delivered_year)}
              </div>
              <div className="text-[10px] text-gray-500">
                {s.revenue.delivered_year_orders} đơn
              </div>
            </div>
          </div>
        </div>

        <div className="bg-teal-50 border-2 border-teal-400 rounded p-4">
          <div className="text-sm text-teal-800 font-semibold">
            🚚 ĐÃ GIAO (theo VTP)
          </div>
          <div className="text-[11px] text-gray-600 mb-2">
            VTP order_successdate — khớp số trên VTP portal
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <div className="text-gray-500">Hôm nay</div>
              <div className="font-mono text-base font-semibold text-teal-900">
                {fmtVND(s.revenue.vtp_delivered_today)}
              </div>
              <div className="text-[10px] text-gray-500">
                {s.revenue.vtp_delivered_today_orders} đơn
                {s.revenue.vtp_delivered_today_orders !== s.revenue.delivered_today_orders && (
                  <span
                    className="ml-1 text-orange-600"
                    title="Chênh với POScake — VTP cron đêm trễ hơn shipper báo"
                  >
                    (Δ {s.revenue.vtp_delivered_today_orders - s.revenue.delivered_today_orders > 0 ? "+" : ""}
                    {s.revenue.vtp_delivered_today_orders - s.revenue.delivered_today_orders})
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="text-gray-500">{monthLabel}</div>
              <div className="font-mono text-base font-semibold text-teal-900">
                {fmtVND(s.revenue.vtp_delivered_month)}
              </div>
              <div className="text-[10px] text-gray-500">
                {s.revenue.vtp_delivered_month_orders} đơn
              </div>
            </div>
            <div>
              <div className="text-gray-500">Năm 2026</div>
              <div className="font-mono text-base font-semibold text-teal-900">
                {fmtVND(s.revenue.vtp_delivered_year)}
              </div>
              <div className="text-[10px] text-gray-500">
                {s.revenue.vtp_delivered_year_orders} đơn
              </div>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border-2 border-orange-400 rounded p-4">
          <div className="text-sm text-orange-800 font-semibold">
            💰 Doanh thu ĐÃ ĐỐI SOÁT (có bảng kê)
          </div>
          <div className="text-[11px] text-gray-600 mb-2">
            ĐVVC đã xác nhận sẽ trả tiền — tiền đã/sắp về tay
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <div className="text-gray-500">Hôm nay</div>
              <div className="font-mono text-base font-semibold text-orange-900">
                {fmtVND(s.revenue.accurate_today)}
              </div>
              <div className="text-[10px] text-gray-500">
                {s.revenue.delivered_today > 0
                  ? `Chênh: ${fmtVND(s.revenue.delivered_today - s.revenue.accurate_today)} chờ`
                  : "—"}
              </div>
            </div>
            <div>
              <div className="text-gray-500">{monthLabel}</div>
              <div className="font-mono text-base font-semibold text-orange-900">
                {fmtVND(s.revenue.accurate_month)}
              </div>
              <div className="text-[10px] text-gray-500">
                Chênh: {fmtVND(s.revenue.delivered_month - s.revenue.accurate_month)} chờ
              </div>
            </div>
            <div>
              <div className="text-gray-500">Năm 2026</div>
              <div className="font-mono text-base font-semibold text-orange-900">
                {fmtVND(s.revenue.accurate_year)}
              </div>
              <div className="text-[10px] text-gray-500">
                Chênh: {fmtVND(s.revenue.delivered_year - s.revenue.accurate_year)} chờ
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-500 mb-3 flex items-center gap-2">
        <span>
          <b className="text-green-700">ĐÃ GIAO</b>: đơn khách nhận (kê khai thuế).{" "}
        </span>
        <span>
          <b className="text-orange-700">ĐÃ ĐỐI SOÁT</b>: đơn đã có bảng kê thu COD (tiền
          đã/sắp về).
        </span>
        <span>
          <b>Chênh</b> = đơn đã giao nhưng ĐVVC chưa phát bảng kê thu COD (in_progress).
        </span>
      </div>

      {/* Breakdown 3 nguồn doanh thu + tổng theo ĐVVC */}
      <div className="bg-white border rounded p-3 mb-4 text-sm">
        <div className="text-xs text-gray-500 mb-2 font-semibold">
          Tổng doanh thu = <span className="text-green-700">3 nguồn cộng lại</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
          {/* 1. POScake */}
          <div className="bg-green-50 border border-green-200 rounded p-2">
            <div className="text-green-700 font-medium">1. POScake</div>
            <div className="font-mono text-lg text-green-800">{fmtVND(s.revenue.year)}</div>
            <div className="text-gray-600 mt-0.5">
              Đơn POScake sync tự động.{" "}
              <Link to="/doanh-thu" className="text-blue-600 hover:underline">
                Xem →
              </Link>
            </div>
          </div>
          {/* 2. Manual TikTok/Shopee */}
          <div className="bg-blue-50 border border-blue-200 rounded p-2">
            <div className="text-blue-700 font-medium">2. Nhập thủ công theo tháng</div>
            <div className="font-mono text-lg text-blue-800">
              {fmtVND(s.revenue.manual_year)}
            </div>
            <div className="text-gray-600 mt-0.5">
              {s.revenue.manual_by_channel.map((c) => `${c.channel} ${fmtVND(c.amount)}`).join(
                ", "
              ) || "Chưa có"}
              .{" "}
              <Link to="/doanh-thu-thu-cong" className="text-blue-600 hover:underline">
                Quản lý →
              </Link>
            </div>
          </div>
          {/* 3. Bảng kê tạo tay */}
          <div className="bg-purple-50 border border-purple-200 rounded p-2">
            <div className="text-purple-700 font-medium">
              3. Bảng kê tạo tay (VTP/J&T)
            </div>
            <div className="font-mono text-lg text-purple-800">
              {fmtVND(s.revenue.carrier_year)}
            </div>
            <div className="text-gray-600 mt-0.5">
              {s.revenue.carrier_year_orders} đơn không qua POScake.{" "}
              {s.revenue.carrier_by_carrier.length > 0 && (
                <>
                  (
                  {s.revenue.carrier_by_carrier
                    .map((c) => `${c.carrier}: ${c.orders} đơn / ${fmtVND(c.amount)}`)
                    .join(", ")}
                  ){" "}
                </>
              )}
              <Link to="/doi-chieu" className="text-blue-600 hover:underline">
                Tab "Bảng kê thừa" →
              </Link>
            </div>
          </div>
          {/* 4. Đã đối soát (ĐVVC + thủ công) */}
          <div className="bg-orange-50 border-2 border-orange-400 rounded p-2">
            <div className="text-orange-700 font-semibold">
              💰 Đã đối soát (ĐVVC + thủ công)
            </div>
            <div className="font-mono text-xl font-bold text-orange-900">
              {fmtVND(s.revenue.accurate_year)}
            </div>
            <div className="text-gray-700 mt-1 text-[11px]">
              <div className="space-y-0.5">
                {s.revenue.revenue_by_carrier.map((c) => (
                  <div key={c.carrier} className="flex justify-between gap-2">
                    <span className="text-orange-700">▸ {c.carrier}:</span>
                    <span className="font-mono">{fmtVND(c.year)}</span>
                  </div>
                ))}
                {s.revenue.revenue_by_carrier.length > 1 && (
                  <div className="flex justify-between gap-2 border-t border-orange-200 pt-0.5 mt-0.5">
                    <span className="text-orange-800 font-semibold">▸ Tổng ĐVVC:</span>
                    <span className="font-mono font-semibold">
                      {fmtVND(
                        s.revenue.revenue_by_carrier.reduce((sum, c) => sum + c.year, 0)
                      )}
                    </span>
                  </div>
                )}
                {s.revenue.manual_by_channel.map((c) => (
                  <div key={c.channel} className="flex justify-between gap-2">
                    <span className="text-blue-700">▸ {c.channel}:</span>
                    <span className="font-mono">{fmtVND(c.amount)}</span>
                  </div>
                ))}
                {s.revenue.manual_by_channel.length > 1 && (
                  <div className="flex justify-between gap-2 border-t border-blue-200 pt-0.5 mt-0.5">
                    <span className="text-blue-800 font-semibold">▸ Tổng thủ công:</span>
                    <span className="font-mono font-semibold">
                      {fmtVND(s.revenue.manual_year)}
                    </span>
                  </div>
                )}
              </div>
              <div className="text-[10px] text-gray-500 mt-1 italic">
                Tiền THỰC nhận — KHÔNG đếm POScake chưa lên bảng kê.
              </div>
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          <b>Sổ S2a-HKD</b> (báo cáo thuế TT152) = nguồn 1 (POScake) + nguồn 3 (bảng kê
          tạo tay từ tháng 1/2026). <b>KHÔNG</b> cộng nguồn 2 (TikTok/Shopee thủ công).
        </div>
      </div>

      {/* Alerts */}
      {(s.alerts.cod_mismatch_count > 0 ||
        s.alerts.no_settlement_count > 0 ||
        s.alerts.overdue_count > 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
          <div className="font-medium text-yellow-900 mb-1">⚠️ Cần kiểm tra</div>
          <div className="text-xs text-gray-600 mb-2">
            Số liệu <b>toàn thời gian</b> (không phụ thuộc kỳ chọn ở trang Đối chiếu).
            Click "Xem chi tiết" để vào trang Đối chiếu xem từng đơn.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            {s.alerts.overdue_count > 0 && (
              <AlertCard
                tone="orange"
                title="⚠️ Trễ đối soát (> 7 ngày)"
                count={s.alerts.overdue_count}
                subTitle={`Chờ COD: ${fmtVND(s.alerts.overdue_pending_cod)} — liên hệ VTP xác nhận`}
                tabLink="/doi-chieu"
              >
                {s.alerts.overdue_samples.length > 0 && (
                  <table className="w-full text-xs mt-2 border-t pt-1">
                    <tbody>
                      {s.alerts.overdue_samples.map((o) => (
                        <tr key={o.order_code} className="border-t border-orange-100">
                          <td className="py-0.5 font-mono">{o.order_code}</td>
                          <td className="py-0.5 text-orange-700 text-right pr-1">
                            {o.days}d
                          </td>
                          <td className="py-0.5 text-right">{fmtVND(o.cod)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </AlertCard>
            )}
            {s.alerts.cod_mismatch_count > 0 && (
              <AlertCard
                tone="red"
                title="Đơn hoàn / lỗi (đã xác nhận)"
                count={s.alerts.cod_mismatch_count}
                subTitle={`Mất ~ ${fmtVND(Math.abs(s.alerts.cod_mismatch_loss))}`}
                tabLink="/doi-chieu"
              >
                {s.alerts.cod_mismatch_samples.length > 0 && (
                  <table className="w-full text-xs mt-2 border-t pt-1">
                    <tbody>
                      {s.alerts.cod_mismatch_samples.map((o) => (
                        <tr key={o.order_code} className="border-t border-red-100">
                          <td className="py-0.5 font-mono">{o.order_code}</td>
                          <td className="py-0.5 text-red-700 text-right pr-1">
                            {o.vtp_status ?? "—"}
                          </td>
                          <td className="py-0.5 text-right">{fmtVND(o.cod)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </AlertCard>
            )}
            {s.alerts.no_settlement_count > 0 && (
              <AlertCard
                tone="blue"
                title="Chưa có bảng kê"
                count={s.alerts.no_settlement_count}
                subTitle={`Chờ thu ${fmtVND(s.alerts.no_settlement_pending_cod)}`}
                tabLink="/doi-chieu"
              >
                {s.alerts.no_settlement_samples.length > 0 && (
                  <table className="w-full text-xs mt-2 border-t pt-1">
                    <tbody>
                      {s.alerts.no_settlement_samples.map((o) => (
                        <tr key={o.order_code} className="border-t border-blue-100">
                          <td className="py-0.5 font-mono">{o.order_code}</td>
                          <td className="py-0.5 text-right">{fmtVND(o.cod)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </AlertCard>
            )}
          </div>
        </div>
      )}

      {/* Doanh thu từng ngày — đáp ứng request "Tổng quan cần xem được từng ngày" */}
      {s.revenue_by_day.length > 0 && (
        <div className="bg-white border rounded mb-4">
          <div className="px-3 py-2 text-sm font-medium border-b bg-gray-50 flex items-center justify-between">
            <span>Doanh thu từng ngày ({monthLabel})</span>
            <Link to="/doanh-thu" className="text-xs text-blue-600 hover:underline">
              Xem chi tiết →
            </Link>
          </div>
          <div className="overflow-x-auto max-h-64">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-1.5">Ngày</th>
                  <th className="text-right px-3 py-1.5">Đơn</th>
                  <th className="text-right px-3 py-1.5">Tiền thực thu (COD)</th>
                </tr>
              </thead>
              <tbody>
                {s.revenue_by_day.map((d) => (
                  <tr key={d.date} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-1">{fmtJustDate(d.date)}</td>
                    <td className="px-3 py-1 text-right">{d.orders}</td>
                    <td className="px-3 py-1 text-right font-medium text-green-700">
                      {fmtVND(d.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Counts grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Tile label="Cửa hàng" value={s.counts.shops} to="/shops" />
        <Tile label="Ngành nghề" value={s.counts.industries} to="/industries" />
        <Tile label="Nhà cung cấp" value={s.counts.suppliers} to="/nha-cung-cap" />
        <Tile label="Sản phẩm" value={s.counts.products} to="/san-pham" />
        <Tile
          label="HĐ đầu vào / tháng"
          value={s.counts.invoices_this_month}
          sub={fmtVND(s.counts.invoices_total_this_month)}
          to="/hoa-don-dau-vao"
        />
        <Tile
          label="Bảng kê / tháng"
          value={s.counts.settlements_this_month}
          to="/bang-ke-vtp"
        />
        <Tile
          label="Đã chốt sổ / tháng"
          value={s.counts.locks_this_month}
          to="/doi-chieu"
        />
        <Tile label="Sổ S2a" value="Xuất Excel" to="/so-s2a" />
      </div>

      {/* Recent locks */}
      {s.recent_locks.length > 0 && (
        <div className="bg-white border rounded p-3">
          <div className="text-sm font-medium mb-2">Các ngày đã chốt sổ gần nhất</div>
          <table className="w-full text-xs">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left py-1">Ngày</th>
                <th className="text-right py-1">Đơn</th>
                <th className="text-right py-1">COD POScake</th>
                <th className="text-right py-1">BK thu về</th>
              </tr>
            </thead>
            <tbody>
              {s.recent_locks.map((l) => (
                <tr key={l.log_date} className="border-t">
                  <td className="py-1">{fmtJustDate(l.log_date)}</td>
                  <td className="py-1 text-right">{l.total_orders}</td>
                  <td className="py-1 text-right">{fmtVND(l.total_cod_pos)}</td>
                  <td className="py-1 text-right text-green-700 font-medium">
                    {fmtVND(l.total_settlement_received)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
  to,
}: {
  label: string;
  value: number | string;
  sub?: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="bg-white border rounded p-3 hover:shadow hover:border-blue-300 transition"
    >
      <div className="text-xs text-gray-600">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </Link>
  );
}

function AlertCard({
  tone,
  title,
  count,
  subTitle,
  tabLink,
  children,
}: {
  tone: "orange" | "red" | "blue";
  title: string;
  count: number;
  subTitle: string;
  tabLink: string;
  children?: React.ReactNode;
}) {
  const ring =
    tone === "orange"
      ? "border-2 border-orange-400"
      : tone === "red"
      ? "border border-red-300"
      : "border border-blue-300";
  const titleClass =
    tone === "orange"
      ? "text-orange-700 font-semibold"
      : tone === "red"
      ? "text-red-700"
      : "text-blue-700";
  const subClass =
    tone === "orange" ? "text-orange-700" : tone === "red" ? "text-red-700" : "text-gray-700";
  const countClass =
    tone === "orange"
      ? "text-orange-800"
      : tone === "red"
      ? "text-red-800"
      : "text-blue-800";
  return (
    <div className={`bg-white ${ring} rounded p-3`}>
      <div className={`text-xs ${titleClass}`}>{title}</div>
      <div className={`text-lg font-semibold ${countClass}`}>{count} đơn</div>
      <div className={`text-xs ${subClass}`}>{subTitle}</div>
      {children}
      <Link to={tabLink} className="text-xs text-gray-500 mt-1 inline-block hover:underline">
        Xem chi tiết →
      </Link>
    </div>
  );
}
